import { useState } from "react";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Check, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useToast } from "@/hooks/use-toast";
import { getCsrfHeaders } from "@/lib/queryClient";
import { BILLING_OWNER_ONLY_MESSAGE } from "@shared/billingPermissions";

interface PlanOption {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  billingCycle: "monthly" | "annual";
  membershipTier: string;
  features: string[];
  recommended?: boolean;
}

function getPlansForRole(role: string): PlanOption[] {
  if (role === "Shipper") {
    return [
      {
        name: "Unlimited",
        monthlyPrice: 25,
        annualPrice: 255,
        billingCycle: "monthly",
        membershipTier: "Paid",
        features: [
          "Post once — your partners are notified automatically",
          "Unlimited shipments per month",
          "Receive bids from partners and the public board",
          "Compare all bids side by side",
          "All communications in one Shipment Workspace",
        ],
        recommended: true,
      },
    ];
  }
  if (role === "Carrier") {
    return [
      {
        name: "Search & Bid",
        monthlyPrice: 25,
        annualPrice: 255,
        billingCycle: "monthly",
        membershipTier: "SearchAndBid",
        features: [
          "Everything in Free",
          "Search the full public load board",
          "Bid on any available load",
          "Advanced load filters",
          "All communications in one Shipment Workspace",
        ],
        recommended: true,
      },
    ];
  }
  if (role === "Broker") {
    return [
      {
        name: "Search & Bid",
        monthlyPrice: 25,
        annualPrice: 255,
        billingCycle: "monthly",
        membershipTier: "SearchAndBid",
        features: [
          "Everything in Free",
          "Search the full public load board",
          "Bid on any available load",
          "Advanced load filters",
        ],
      },
      {
        name: "Search, Bid & Post",
        monthlyPrice: 45,
        annualPrice: 459,
        billingCycle: "monthly",
        membershipTier: "Paid",
        features: [
          "Everything in Search & Bid",
          "Post loads on behalf of shipper clients",
          "Full marketplace toolset",
          "All communications in one Shipment Workspace",
        ],
        recommended: true,
      },
    ];
  }
  return [];
}

export default function Upgrade() {
  const { user, isTeamMember } = useAuth();
  const { data: subStatus } = useSubscriptionStatus();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const role = user?.userType || "Shipper";
  const plans = getPlansForRole(role);
  const tier = user?.membershipTier || "Free";
  const isCancelled = tier === "Cancelled";
  const isSuspended = tier === "Suspended";
  const isPaidTier = ["Paid", "Annual", "SearchAndBid"].includes(tier);
  const planTier = tier === "Annual" ? (role === "Carrier" ? "SearchAndBid" : "Paid") : tier;
  const currentPlan = isPaidTier
    ? plans.find(plan => plan.membershipTier === planTier) || (plans.length === 1 ? plans[0] : undefined)
    : undefined;
  const currentPlanName = currentPlan ? `${role} ${currentPlan.name}` : tier;
  const currentStatus = isCancelled ? "Cancelled"
    : isSuspended || (isPaidTier && subStatus?.paymentProblem) ? "Payment problem" : "Active";
  const requiresBilling = isSuspended || (!!user?.stripeSubscriptionId && !isCancelled);

  const handleManageBilling = async () => {
    if (isTeamMember) return;
    setOpeningPortal(true);
    try {
      const res = await fetch("/api/stripe/create-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || "Failed to open billing portal");
      window.location.href = data.url;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setOpeningPortal(false);
    }
  };

  const handleUpgrade = async (plan: PlanOption) => {
    if (!user || isTeamMember || requiresBilling) return;
    setLoadingTier(plan.membershipTier);
    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({
          billingCycle: isAnnual ? "annual" : "monthly",
          membershipTier: plan.membershipTier,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to start checkout");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setLoadingTier(null);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Upgrade your plan</h1>
        <p className="text-muted-foreground">Choose the plan that works for your business.</p>
      </div>

      {/* Current plan */}
      <Card className="p-4 mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-0.5">Current plan</p>
          <p className="font-medium">{currentPlanName}</p>
          {currentPlan && (
            <p className="text-sm text-muted-foreground">
              {tier === "Annual" || user?.billingCycle === "annual" ? "Annual billing" : "Monthly billing"}
            </p>
          )}
        </div>
        <Badge variant="secondary" data-testid="badge-billing-status">{currentStatus}</Badge>
      </Card>

      {isTeamMember && <p className="mb-6">{BILLING_OWNER_ONLY_MESSAGE}</p>}
      {!isTeamMember && isCancelled && <p className="mb-6">Your plan was cancelled</p>}
      {!isTeamMember && requiresBilling && (
        <div className="mb-6 space-y-3">
          <p>
            {isSuspended
              ? "There's a payment problem with your subscription. Update your payment details in Manage Billing."
              : "Your account already has a subscription. Plan changes are made in Manage Billing."}
          </p>
          <Button onClick={handleManageBilling} disabled={openingPortal} data-testid="button-manage-billing">
            {openingPortal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Manage Billing
          </Button>
        </div>
      )}

      {!isTeamMember && <>
      {/* Billing toggle */}
      <div className="flex items-center gap-3 mb-6">
        <span className={`text-sm ${!isAnnual ? "font-medium text-foreground" : "text-muted-foreground"}`}>
          Monthly
        </span>
        <Switch
          checked={isAnnual}
          onCheckedChange={setIsAnnual}
          data-testid="toggle-billing-cycle"
          aria-label="Toggle annual billing"
        />
        <span className={`text-sm ${isAnnual ? "font-medium text-foreground" : "text-muted-foreground"}`}>
          Annual
        </span>
        {isAnnual && (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs no-default-hover-elevate no-default-active-elevate">
            Save 15%
          </Badge>
        )}
      </div>

      {/* Plan cards */}
      <div className={`grid gap-4 ${plans.length > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}>
        {plans.map((plan) => (
          <Card
            key={plan.membershipTier}
            className={`p-5 flex flex-col ${plan.recommended ? "border-primary border-2" : ""}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-lg">{plan.name}</p>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-3xl font-bold">
                    ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {isAnnual ? "/year" : "/month"}
                  </span>
                </div>
                {isAnnual && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ${(plan.annualPrice / 12).toFixed(2)}/mo billed annually
                  </p>
                )}
              </div>
              {plan.recommended && (
                <Badge className="bg-primary/10 text-primary border-primary/20 text-xs no-default-hover-elevate no-default-active-elevate">
                  Recommended
                </Badge>
              )}
            </div>

            <ul className="space-y-2 mb-6 flex-1">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>

            {!isTeamMember && !requiresBilling && <Button
              className="w-full"
              onClick={() => handleUpgrade(plan)}
              disabled={!user || loadingTier !== null}
              data-testid={`button-upgrade-${plan.membershipTier.toLowerCase()}`}
            >
              {loadingTier === plan.membershipTier ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                `Upgrade to ${plan.name} — $${isAnnual ? plan.annualPrice : plan.monthlyPrice}/${isAnnual ? "yr" : "mo"}`
              )}
            </Button>}
          </Card>
        ))}
      </div>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Cancel anytime · Secure payment via Stripe
      </p>
      </>}
    </div>
  );
}
